import { zodResolver } from '@hookform/resolvers/zod';
import {
  CreatePackageRequestSchema,
  PACKAGE_MONTH_NAMES,
  normalizePreferredMonths,
  slugify,
  type CreatePackageDayRequest,
  type CreatePackageFaqRequest,
  type CreatePackageFlightRequest,
  type CreatePackageHotelRequest,
  type CreatePackageImageRequest,
  type CreatePackageRequest,
  type PackageDetail,
  type PackageStatus,
  type UpdatePackageRequest,
} from '@utazzo/shared';
import {
  UzAlert,
  UzButton,
  UzCombobox,
  UzForm,
  UzFormField,
  UzInput,
  UzNumberInput,
  UzSelect,
  UzSwitch,
  UzTabs,
  UzTextarea,
} from '@utazzo/ui';
import { ArrowLeft, Wand2 } from 'lucide-react';
import React, { type ReactNode, useEffect, useMemo, useRef, useState } from 'react';
import {
  useForm,
  useWatch,
  type DefaultValues,
  type Resolver,
  type UseFormReturn,
} from 'react-hook-form';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'sonner';

import { BatchesVariantsEditor, type PackageBatchDraft } from '../components/BatchesVariantsEditor';
import { CollectionsEditor } from '../components/CollectionsEditor';
import { DaysEditor } from '../components/DaysEditor';
import { ExclusionsEditor } from '../components/ExclusionsEditor';
import { FaqsEditor } from '../components/FaqsEditor';
import { FlightsEditor } from '../components/FlightsEditor';
import { HeroHighlightsEditor } from '../components/HeroHighlightsEditor';
import { HotelsEditor } from '../components/HotelsEditor';
import { ImagesTab } from '../components/ImagesTab';
import { InclusionsEditor } from '../components/InclusionsEditor';
import { PackageCard } from '../components/PackageCard';
import { PaymentTermsEditor } from '../components/PaymentTermsEditor';
import {
  formatPackageValidationToast,
  handlePackageFormValidationErrors,
} from '../package-form-validation';
import * as packagesApi from '../packages.api';
import { usePackageDetail, useUpdatePackage } from '../use-packages';

import { useDestinationsList } from '@/features/destinations/use-destinations';
import { useTripTypesList } from '@/features/trip-types/use-trip-types';
import { MediaDropField } from '@/features/website-cms/components/MediaDropField';
import { RichTextEditor } from '@/features/website-cms/components/RichTextEditor';
import { useListReturn } from '@/hooks/use-list-return';

const STATUS_META: Record<PackageStatus, { dot: string; label: string }> = {
  DRAFT: { dot: 'bg-zinc-400', label: 'Draft' },
  ACTIVE: { dot: 'bg-emerald-500', label: 'Active' },
  INACTIVE: { dot: 'bg-amber-500', label: 'Inactive' },
  ARCHIVED: { dot: 'bg-rose-500', label: 'Archived' },
};

const STATUS_OPTIONS: ReadonlyArray<{ value: PackageStatus; label: string }> = [
  { value: 'DRAFT', label: 'Draft' },
  { value: 'ACTIVE', label: 'Active' },
  { value: 'INACTIVE', label: 'Inactive' },
  { value: 'ARCHIVED', label: 'Archived' },
];

// Full month names - stored verbatim in the DB and shown on the website.
const MONTHS = PACKAGE_MONTH_NAMES;

// Text fields where an empty string must be persisted as null.
const NULLABLE_TEXT_KEYS = [
  'shortDescription',
  'packageCategory',
  'packageTheme',
  'overviewHtml',
  'visaInsuranceHtml',
  'costSummaryHtml',
  'metaTitle',
  'metaDescription',
  'metaKeywords',
] as const;

/** Normalise a rupee amount to a string with up to 2 decimals. 25000 → "25000", 24999.5 → "24999.5". */
const moneyStr = (n: number): string => String(Math.round(n * 100) / 100);

const formatRupees = (rupees: number): string =>
  `₹${rupees.toLocaleString('en-IN', { maximumFractionDigits: 2 })}`;

/** Digits with an optional decimal point and at most 2 decimal places (or empty). */
const MONEY_2DP_RE = /^\d*(\.\d{0,2})?$/;

const toForm = (d: PackageDetail): CreatePackageRequest => ({
  slug: d.slug,
  name: d.name,
  shortDescription: d.shortDescription ?? '',
  packageCategory: d.packageCategory ?? '',
  packageTheme: d.packageTheme ?? '',
  tripTypeId: d.tripTypeId ?? null,
  destinationId: d.destinationId ?? null,
  isInternational: d.isInternational,
  isMice: d.isMice,
  preferredMonths: normalizePreferredMonths(d.preferredMonths),
  rating: d.rating,
  numberOfNights: d.numberOfNights,
  numberOfDays: d.numberOfDays,
  price: d.price,
  strikePrice: d.strikePrice,
  hotelCost: d.hotelCost,
  flightCost: d.flightCost,
  overviewHtml: d.overviewHtml ?? '',
  visaInsuranceHtml: d.visaInsuranceHtml ?? '',
  paymentTerms: d.paymentTerms.length > 0 ? d.paymentTerms : [{ label: '', value: '' }],
  costSummaryHtml: d.costSummaryHtml ?? '',
  status: d.status,
  metaTitle: d.metaTitle ?? '',
  metaDescription: d.metaDescription ?? '',
  metaKeywords: d.metaKeywords ?? '',
  ogImageId: d.ogImageId ?? null,
});

// Blank "create" form - every field empty so nothing is pre-filled.
const CREATE_DEFAULTS: DefaultValues<CreatePackageRequest> = {
  slug: '',
  name: '',
  shortDescription: '',
  packageCategory: '',
  packageTheme: '',
  tripTypeId: null,
  destinationId: null,
  isInternational: false,
  isMice: false,
  preferredMonths: [],
  rating: null,
  numberOfNights: undefined,
  numberOfDays: undefined,
  price: '',
  strikePrice: null,
  hotelCost: null,
  flightCost: null,
  overviewHtml: '',
  visaInsuranceHtml: '',
  paymentTerms: [{ label: '', value: '' }],
  costSummaryHtml: '',
  status: 'DRAFT',
  metaTitle: '',
  metaDescription: '',
  metaKeywords: '',
  ogImageId: null,
};

/** Derive the read-only nights/days from the itinerary day count. */
const deriveNightsDays = (dayCount: number): { numberOfDays: number; numberOfNights: number } => {
  // A package is at least 1 day, so floor at 1 - this keeps the auto value valid
  // against the schema (numberOfDays >= 1) even before any itinerary day is added.
  const numberOfDays = Math.max(1, dayCount);
  return { numberOfDays, numberOfNights: Math.max(0, numberOfDays - 1) };
};

const errMsg = (e: unknown): string | null =>
  e && typeof e === 'object' && 'message' in e
    ? String((e as { message?: unknown }).message)
    : null;

const GROUP_COMMUNITY_RE = /group|community/i;

const isBatchTripType = (tripType: { code: string; name: string } | undefined): boolean =>
  tripType !== undefined &&
  (GROUP_COMMUNITY_RE.test(tripType.code) || GROUP_COMMUNITY_RE.test(tripType.name));

type PackageTab =
  | 'details'
  | 'batches'
  | 'itinerary'
  | 'inclusions'
  | 'logistics'
  | 'content'
  | 'seo';

export const PackageDetailPage = (): React.JSX.Element => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const backToList = useListReturn('/packages');
  const isNew = id === 'new';

  const detailQuery = usePackageDetail(isNew ? undefined : (id ?? undefined));
  const pkg = detailQuery.data ?? null;
  const status = pkg ? STATUS_META[pkg.status] : null;

  const [activeTab, setActiveTab] = useState<PackageTab>('details');

  const updatePkg = useUpdatePackage();

  // Create-mode draft: the child collections staged in memory until "Create package".
  const [draft, setDraft] = useState({
    days: [] as CreatePackageDayRequest[],
    inclusionIds: [] as string[],
    exclusionIds: [] as string[],
    collectionIds: [] as string[],
    heroInclusionIds: [] as string[],
    hotels: [] as CreatePackageHotelRequest[],
    flights: [] as CreatePackageFlightRequest[],
    faqs: [] as CreatePackageFaqRequest[],
    images: [] as CreatePackageImageRequest[],
    batches: [] as PackageBatchDraft[],
  });
  const [creating, setCreating] = useState(false);

  const form = useForm<CreatePackageRequest>({
    resolver: zodResolver(CreatePackageRequestSchema) as Resolver<CreatePackageRequest>,
    defaultValues: CREATE_DEFAULTS,
  });

  const tripTypesQuery = useTripTypesList({
    page: 1,
    pageSize: 100,
    sortBy: 'sortOrder',
    sortDir: 'asc',
    isActive: true,
  });
  const fitTripTypeId = useMemo(
    () =>
      tripTypesQuery.data?.data.find(
        (t) => t.code.toLowerCase() === 'fit' || t.name.toLowerCase() === 'fit',
      )?.id ?? null,
    [tripTypesQuery.data],
  );
  const tripTypeOptions = useMemo(() => {
    const rows = tripTypesQuery.data?.data ?? [];
    const options = rows.map((t) => ({ value: t.id, label: t.name }));
    if (!isNew) {
      return [{ value: '', label: '- None -' }, ...options];
    }
    return options;
  }, [tripTypesQuery.data, isNew]);

  const watchedTripTypeId =
    (useWatch({ control: form.control, name: 'tripTypeId' }) as string | null | undefined) ?? null;
  const selectedTripType = useMemo(
    () => tripTypesQuery.data?.data.find((t) => t.id === watchedTripTypeId),
    [tripTypesQuery.data, watchedTripTypeId],
  );
  const showBatchesTab = isBatchTripType(selectedTripType);

  useEffect(() => {
    if (activeTab === 'batches' && !showBatchesTab) setActiveTab('details');
  }, [activeTab, showBatchesTab]);

  // Group / community pricing lives on batch variants — clear package-level price fields.
  useEffect(() => {
    if (!showBatchesTab) return;
    setPriceRupees('0');
    setDiscountRupees('');
    form.setValue('price', '0', { shouldDirty: true });
    form.setValue('strikePrice', null, { shouldDirty: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps -- stable form methods
  }, [showBatchesTab, watchedTripTypeId]);

  // The package's selected destination - drives the day editor's city picker.
  const watchedDestinationId =
    (useWatch({ control: form.control, name: 'destinationId' }) as string | null | undefined) ??
    null;

  // `isInternational` has no UI toggle - it's derived from the selected
  // destination's country: India → domestic (false), anything else → true.
  // (Same list query as DestinationField, so React Query de-dupes the fetch.)
  const destinationsForFlag = useDestinationsList({
    page: 1,
    pageSize: 200,
    sortBy: 'name',
    sortDir: 'asc',
    isActive: true,
  });
  useEffect(() => {
    const list = destinationsForFlag.data?.data ?? [];
    if (watchedDestinationId) {
      const dest = list.find((d) => d.id === watchedDestinationId);
      if (!dest) return; // destinations not loaded yet - wait for the list
      const isIndia = (dest.country?.name ?? '').trim().toLowerCase() === 'india';
      form.setValue('isInternational', !isIndia);
    } else {
      form.setValue('isInternational', false);
    }
  }, [watchedDestinationId, destinationsForFlag.data, form]);

  // Price / Discount are local rupee inputs. We persist price = total − discount
  // and strikePrice = total (original), so the website shows the original crossed-out.
  const [priceRupees, setPriceRupees] = useState('');
  const [discountRupees, setDiscountRupees] = useState('');

  // Seed / reset the form whenever we switch between create and a loaded package.
  useEffect(() => {
    if (isNew) {
      form.reset({ ...CREATE_DEFAULTS, tripTypeId: fitTripTypeId });
      setPriceRupees('');
      setDiscountRupees('');
    } else if (pkg) {
      form.reset(toForm(pkg));
      if (pkg.strikePrice) {
        // strikePrice is the original (total) price; discount = original − selling.
        const disc = Number(pkg.strikePrice) - Number(pkg.price);
        if (disc > 0) {
          setPriceRupees(pkg.strikePrice);
          setDiscountRupees(moneyStr(disc));
        } else {
          // No real discount — treat as plain price so the website won't show a strike.
          setPriceRupees(pkg.price);
          setDiscountRupees('');
          form.setValue('strikePrice', null, { shouldDirty: false });
        }
      } else {
        setPriceRupees(pkg.price);
        setDiscountRupees('');
      }
    }
    // `form` methods are stable; `fitTripTypeId` is applied in the effect below
    // when trip types finish loading after the first create reset.
    // eslint-disable-next-line react-hooks/exhaustive-deps -- reset on route / package change only
  }, [isNew, pkg?.id]);

  // Trip types load async — apply FIT once the master list is available on create.
  useEffect(() => {
    if (!isNew || !fitTripTypeId) return;
    const existing = form.getValues('tripTypeId');
    if (existing === null || existing === undefined) {
      form.setValue('tripTypeId', fitTripTypeId, { shouldDirty: false });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- stable form methods
  }, [isNew, fitTripTypeId]);

  // In edit mode, seed the bulk-set drafts (inclusions / exclusions / collections /
  // hero highlights) from the loaded package. These sections no longer have their
  // own per-section Save buttons - the single header Save persists them. Seed ONCE
  // per package id: a background refetch (e.g. window refocus, or the post-save
  // invalidation) yields a fresh pkg object, and re-seeding then would wipe the
  // operator's unsaved selection changes. The id guard resets on navigation
  // (the page remounts), so a different package always re-seeds.
  const seededBulkPkgId = useRef<string | null>(null);
  useEffect(() => {
    if (isNew || !pkg) return;
    if (seededBulkPkgId.current === pkg.id) return;
    seededBulkPkgId.current = pkg.id;
    setDraft((d) => ({
      ...d,
      inclusionIds: pkg.inclusions
        .filter((i) => i.inclusionExclusionId)
        .map((i) => i.inclusionExclusionId as string),
      exclusionIds: pkg.exclusions
        .filter((e) => e.inclusionExclusionId)
        .map((e) => e.inclusionExclusionId as string),
      collectionIds: pkg.collections.map((c) => c.marketingTagId),
      heroInclusionIds: pkg.heroInclusions.map((h) => h.heroInclusionId),
    }));
  }, [isNew, pkg]);

  // Nights/Days are derived from the itinerary, not edited. Edit mode falls back to
  // the stored numberOfDays for legacy packages with no day-rows so saving doesn't
  // clobber it to 0; create mode uses the staged draft length.
  const dayCount = isNew
    ? draft.days.length
    : pkg
      ? pkg.days.length > 0
        ? pkg.days.length
        : pkg.numberOfDays
      : 0;
  const { numberOfDays, numberOfNights } = deriveNightsDays(dayCount);

  // Keep the derived counts on the form so the resolver (which requires them) validates
  // and the submitted payload carries them.
  useEffect(() => {
    form.setValue('numberOfDays', numberOfDays);
    form.setValue('numberOfNights', numberOfNights);
  }, [form, numberOfDays, numberOfNights]);

  // Price / Discount are local rupee inputs. We persist price = total − discount
  // and strikePrice = total (original) so the website shows the original crossed-out.
  // Values are stored as plain rupees (no paise conversion).
  const applyPricing = (origStr: string, discStr: string): void => {
    if (origStr.trim() === '') {
      form.setValue('price', '', { shouldDirty: true, shouldValidate: true });
      form.setValue('strikePrice', null, { shouldDirty: true });
      return;
    }
    const orig = Number(origStr);
    const disc = Number(discStr.trim() === '' ? '0' : discStr);
    if (Number.isFinite(orig) && disc > 0) {
      form.setValue('strikePrice', origStr, { shouldDirty: true });
      form.setValue('price', moneyStr(Math.max(0, orig - disc)), {
        shouldDirty: true,
        shouldValidate: true,
      });
    } else {
      form.setValue('strikePrice', null, { shouldDirty: true });
      form.setValue('price', origStr, { shouldDirty: true, shouldValidate: true });
    }
  };

  const onPriceChange = (v: string): void => {
    if (!MONEY_2DP_RE.test(v)) return; // accept up to 2 decimal places only
    setPriceRupees(v);
    applyPricing(v, discountRupees);
  };
  const onDiscountChange = (v: string): void => {
    if (!MONEY_2DP_RE.test(v)) return;
    setDiscountRupees(v);
    applyPricing(priceRupees, v);
  };

  const handleCreate = async (details: CreatePackageRequest): Promise<void> => {
    setCreating(true);
    // Phase 1: create the package itself. A failure here (e.g. duplicate slug)
    // means nothing was persisted - surface it and let the user fix + retry.
    let newId: string;
    try {
      const created = await packagesApi.createPackage(details);
      newId = created.id;
    } catch (e) {
      toast.error(errMsg(e) ?? 'Failed to create package');
      setCreating(false);
      return;
    }
    // Phase 2: attach the staged children. The package already exists, so even
    // if a child step fails we navigate into it (edit mode) rather than leave
    // the user to re-submit and hit a duplicate-slug conflict.
    try {
      await Promise.all([
        draft.inclusionIds.length
          ? packagesApi.setPackageInclusions(newId, {
              items: draft.inclusionIds.map((inclusionExclusionId) => ({ inclusionExclusionId })),
            })
          : Promise.resolve(),
        draft.exclusionIds.length
          ? packagesApi.setPackageExclusions(newId, {
              items: draft.exclusionIds.map((inclusionExclusionId) => ({ inclusionExclusionId })),
            })
          : Promise.resolve(),
        draft.collectionIds.length
          ? packagesApi.setPackageCollections(newId, { marketingTagIds: draft.collectionIds })
          : Promise.resolve(),
        draft.heroInclusionIds.length
          ? packagesApi.setPackageHeroInclusions(newId, {
              heroInclusionIds: draft.heroInclusionIds,
            })
          : Promise.resolve(),
      ]);
      // Sequential per collection to preserve order / sortOrder.
      for (const day of draft.days) {
        await packagesApi.createPackageDay(newId, day);
      }
      for (const hotel of draft.hotels) {
        await packagesApi.createPackageHotel(newId, hotel);
      }
      for (const flight of draft.flights) {
        await packagesApi.createPackageFlight(newId, flight);
      }
      for (const faq of draft.faqs) {
        await packagesApi.createPackageFaq(newId, faq);
      }
      for (const image of draft.images) {
        await packagesApi.createPackageImage(newId, image);
      }
      for (const item of draft.batches) {
        const createdBatch = await packagesApi.createPackageBatch(newId, item.batch);
        for (const variant of item.variants) {
          await packagesApi.createPackageVariant(newId, createdBatch.id, variant);
        }
      }
      toast.success('Package created');
    } catch (e) {
      toast.error(
        `Package created, but some sections failed to save: ${errMsg(e) ?? 'unknown error'}. Please review.`,
      );
    } finally {
      setCreating(false);
      navigate(`/packages/${newId}`);
    }
  };

  // Header Save / Create. Edit → PATCH the typed fields. Create → batch orchestration.
  const submit = form.handleSubmit(
    async (values) => {
      // Total price must always exceed the hotel + flight cost components (FIT / cruise etc.).
      const totalRupees = priceRupees.trim() === '' ? 0 : Number(priceRupees);
      const costFloor = Number(values.hotelCost ?? 0) + Number(values.flightCost ?? 0);
      if (!showBatchesTab && costFloor > 0 && totalRupees <= costFloor) {
        form.setError('price', {
          type: 'manual',
          message: `Total price must exceed hotel + flight cost (${formatRupees(costFloor)})`,
        });
        setActiveTab('details');
        toast.error(
          `Please fix "Total price (₹)": Total price must exceed hotel + flight cost (${formatRupees(costFloor)})`,
        );
        return;
      }

      const cleaned: Record<string, unknown> = { ...values, numberOfDays, numberOfNights };
      for (const key of NULLABLE_TEXT_KEYS) {
        const v = cleaned[key];
        if (typeof v === 'string' && v.trim() === '') cleaned[key] = null;
      }
      cleaned.paymentTerms = (values.paymentTerms ?? []).filter(
        (t) => t.label.trim() !== '' && t.value.trim() !== '',
      );
      cleaned.preferredMonths = normalizePreferredMonths(values.preferredMonths);
      if (isNew) {
        await handleCreate(cleaned as CreatePackageRequest);
      } else if (pkg) {
        // The bulk-set sections (inclusions / exclusions / collections / hero
        // highlights) no longer have their own Save buttons - persist them here
        // first (each call replaces the full list, empty clears it), then PATCH
        // the core fields. updatePkg invalidates the detail query, which re-seeds
        // the section drafts from the saved state.
        try {
          await Promise.all([
            packagesApi.setPackageInclusions(pkg.id, {
              items: draft.inclusionIds.map((inclusionExclusionId) => ({ inclusionExclusionId })),
            }),
            packagesApi.setPackageExclusions(pkg.id, {
              items: draft.exclusionIds.map((inclusionExclusionId) => ({ inclusionExclusionId })),
            }),
            packagesApi.setPackageCollections(pkg.id, { marketingTagIds: draft.collectionIds }),
            packagesApi.setPackageHeroInclusions(pkg.id, {
              heroInclusionIds: draft.heroInclusionIds,
            }),
          ]);
        } catch (e) {
          toast.error(`Failed to save sections: ${errMsg(e) ?? 'unknown error'}`);
          return;
        }
        await updatePkg.mutateAsync({ id: pkg.id, payload: cleaned as UpdatePackageRequest });
        form.reset(values);
      }
    },
    (errors) => {
      const collected = handlePackageFormValidationErrors(errors, setActiveTab);
      toast.error(formatPackageValidationToast(collected));
    },
  );

  const pending = isNew ? creating : updatePkg.isPending;

  const tabs = useMemo(() => {
    const list: { value: PackageTab; label: ReactNode; content: ReactNode }[] = [
      {
        value: 'details',
        label: 'Details',
        content: (
          <div className="space-y-6">
            <UzForm form={form} onSubmit={submit}>
              <div className="grid items-start gap-6 lg:grid-cols-[minmax(0,1fr)_320px]">
                <div className="min-w-0 space-y-6">
                  <BasicsCard
                    form={form}
                    pending={pending}
                    tripTypesLoading={tripTypesQuery.isLoading}
                    tripTypeOptions={tripTypeOptions}
                    numberOfNights={numberOfNights}
                    numberOfDays={numberOfDays}
                  />
                  <PricingCard
                    form={form}
                    pending={pending}
                    hidePackagePrice={showBatchesTab}
                    priceRupees={priceRupees}
                    discountRupees={discountRupees}
                    onPriceChange={onPriceChange}
                    onDiscountChange={onDiscountChange}
                  />
                </div>
                <aside className="space-y-6">
                  <StatusCard pending={pending} />
                </aside>
              </div>
            </UzForm>
            <div className="grid items-start gap-6 lg:grid-cols-[minmax(0,1fr)_320px]">
              <PackageCard
                title="Tags & highlights"
                description="Browse tags and hero highlights shown on the website."
              >
                <div className="space-y-6">
                  <CollectionsEditor
                    draftValue={draft.collectionIds}
                    onDraftChange={(collectionIds) => setDraft((d) => ({ ...d, collectionIds }))}
                  />
                  <HeroHighlightsEditor
                    draftValue={draft.heroInclusionIds}
                    onDraftChange={(heroInclusionIds) =>
                      setDraft((d) => ({ ...d, heroInclusionIds }))
                    }
                  />
                </div>
              </PackageCard>
            </div>
          </div>
        ),
      },
      {
        value: 'itinerary',
        label: 'Itinerary',
        content: (
          <div className="space-y-6">
            <PackageCard title="Day-by-day">
              {pkg ? (
                <DaysEditor pkg={pkg} destinationId={watchedDestinationId} />
              ) : (
                <DaysEditor
                  draftValue={draft.days}
                  onDraftChange={(days) => setDraft((d) => ({ ...d, days }))}
                  destinationId={watchedDestinationId}
                />
              )}
            </PackageCard>
          </div>
        ),
      },
      ...(showBatchesTab
        ? [
            {
              value: 'batches' as const,
              label: 'Batches & variants',
              content: (
                <PackageCard
                  title="Batches & variants"
                  description="Fixed departures and pricing options for group or community packages."
                >
                  {pkg ? (
                    <BatchesVariantsEditor
                      pkg={pkg}
                      numberOfDays={numberOfDays}
                      hasItinerary={dayCount > 0}
                    />
                  ) : (
                    <BatchesVariantsEditor
                      draftValue={draft.batches}
                      onDraftChange={(batches) => setDraft((d) => ({ ...d, batches }))}
                      numberOfDays={numberOfDays}
                      hasItinerary={dayCount > 0}
                    />
                  )}
                </PackageCard>
              ),
            },
          ]
        : []),
      {
        value: 'inclusions',
        label: 'Inclusions & Exclusions',
        content: (
          <div className="space-y-6">
            <div className="grid items-start gap-6 lg:grid-cols-2">
              <PackageCard title="Inclusions">
                <InclusionsEditor
                  draftValue={draft.inclusionIds}
                  onDraftChange={(inclusionIds) => setDraft((d) => ({ ...d, inclusionIds }))}
                />
              </PackageCard>
              <PackageCard title="Exclusions">
                <ExclusionsEditor
                  draftValue={draft.exclusionIds}
                  onDraftChange={(exclusionIds) => setDraft((d) => ({ ...d, exclusionIds }))}
                />
              </PackageCard>
            </div>
            <UzForm form={form} onSubmit={submit}>
              <PackageCard
                title="Other Info"
                description="Extra notes shown in the package’s Other Info section on the website."
              >
                <UzFormField name="costSummaryHtml">
                  {({ field }) => (
                    <RichTextEditor
                      value={(field.value as string | null | undefined) ?? ''}
                      onChange={field.onChange}
                      folder="packages"
                      disabled={pending}
                    />
                  )}
                </UzFormField>
              </PackageCard>
            </UzForm>
          </div>
        ),
      },
      {
        value: 'logistics',
        label: 'Logistics',
        content: (
          <div className="space-y-6">
            <PackageCard title="Hotels">
              {pkg ? (
                <HotelsEditor pkg={pkg} />
              ) : (
                <HotelsEditor
                  draftValue={draft.hotels}
                  onDraftChange={(hotels) => setDraft((d) => ({ ...d, hotels }))}
                />
              )}
            </PackageCard>
            <PackageCard title="Flights">
              {pkg ? (
                <FlightsEditor pkg={pkg} />
              ) : (
                <FlightsEditor
                  draftValue={draft.flights}
                  onDraftChange={(flights) => setDraft((d) => ({ ...d, flights }))}
                />
              )}
            </PackageCard>
          </div>
        ),
      },
      {
        value: 'content',
        label: 'Content',
        content: (
          <UzForm form={form} onSubmit={submit}>
            <div className="mx-auto max-w-3xl space-y-6">
              <DescriptionsCard pending={pending} />
              <PackageCard
                title="Payment terms"
                description="Payment milestones shown as Payment Options & Schedule on the public page."
              >
                <PaymentTermsEditor form={form} disabled={pending} />
              </PackageCard>
              <PackageCard title="FAQs">
                {pkg ? (
                  <FaqsEditor pkg={pkg} />
                ) : (
                  <FaqsEditor
                    draftValue={draft.faqs}
                    onDraftChange={(faqs) => setDraft((d) => ({ ...d, faqs }))}
                  />
                )}
              </PackageCard>
              <PackageCard title="Photos">
                {pkg ? (
                  <ImagesTab pkg={pkg} />
                ) : (
                  <ImagesTab
                    galleryDraft={draft.images}
                    onGalleryDraftChange={(images) => setDraft((d) => ({ ...d, images }))}
                  />
                )}
              </PackageCard>
            </div>
          </UzForm>
        ),
      },
      {
        value: 'seo',
        label: 'SEO',
        content: (
          <UzForm form={form} onSubmit={submit}>
            <div className="mx-auto max-w-3xl space-y-6">
              <SeoCard pending={pending} />
            </div>
          </UzForm>
        ),
      },
    ];
    return list;
    // eslint-disable-next-line react-hooks/exhaustive-deps -- submit/handlers stable per render
  }, [
    isNew,
    pkg,
    form,
    pending,
    numberOfNights,
    numberOfDays,
    dayCount,
    priceRupees,
    discountRupees,
    draft,
    watchedDestinationId,
    tripTypeOptions,
    tripTypesQuery.isLoading,
    showBatchesTab,
    selectedTripType,
  ]);

  return (
    <div className="-mx-4 -mt-6 flex min-h-full flex-col lg:-mx-8 lg:-mt-8">
      <div className="sticky -top-6 z-30 flex h-12 items-center gap-3 border-b border-zinc-200 bg-white px-6 lg:-top-8 lg:px-8">
        <button
          type="button"
          onClick={backToList}
          className="-ml-1 inline-flex h-7 w-7 items-center justify-center rounded text-zinc-500 transition-colors hover:bg-zinc-100 hover:text-zinc-900"
          aria-label="Back to packages"
        >
          <ArrowLeft className="h-4 w-4" />
        </button>
        <div className="flex min-w-0 items-center gap-2">
          <span className="text-xs font-medium text-zinc-500">Packages</span>
          <span className="text-zinc-300" aria-hidden>
            /
          </span>
          <span className="truncate text-sm font-semibold tracking-tight text-zinc-900">
            {isNew ? 'New package' : (pkg?.name ?? 'Package')}
          </span>
        </div>
        <span className="flex-1" />
        {!isNew && status && (
          <span className="inline-flex items-center gap-1.5 text-xs text-zinc-600">
            <span className={'h-1.5 w-1.5 rounded-full ' + status.dot} aria-hidden />
            {status.label}
          </span>
        )}
        <UzButton variant="ghost" size="sm" onClick={backToList} disabled={pending}>
          Cancel
        </UzButton>
        <UzButton size="sm" onClick={submit} loading={pending}>
          <span>{isNew ? 'Create package' : 'Save'}</span>
          <kbd className="ml-2 hidden h-4 items-center rounded border border-zinc-300/60 bg-white/15 px-1 font-mono text-[10px] text-current/80 sm:inline-flex">
            ⌘S
          </kbd>
        </UzButton>
      </div>

      <div className="mx-auto w-full max-w-6xl min-w-0 px-6 py-6 lg:px-8">
        {!isNew && detailQuery.isLoading && (
          <div className="space-y-4">
            <div className="h-5 w-2/3 animate-pulse rounded bg-zinc-100" />
            <div className="h-64 animate-pulse rounded-lg border border-zinc-200 bg-zinc-50/60" />
          </div>
        )}
        {detailQuery.isError && (
          <UzAlert tone="destructive">
            Failed to load the package. It may have been archived or deleted.
          </UzAlert>
        )}

        {(isNew || pkg) && (
          <UzTabs
            value={activeTab}
            onValueChange={(v) => setActiveTab(v as PackageTab)}
            tabs={tabs}
            // Phones: the strip scrolls sideways instead of running off-screen.
            listClassName="max-sm:max-w-full max-sm:justify-start max-sm:overflow-x-auto"
          />
        )}
      </div>
    </div>
  );
};

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

interface CardProps {
  pending: boolean;
}

// ---------------------------------------------------------------------------
// Details - main cards
// ---------------------------------------------------------------------------

/** Single trip-category picker (from the Trip Types master) bound to the form. */
const TripCategoryField = ({
  pending,
  tripTypesLoading,
  tripTypeOptions,
}: CardProps & {
  tripTypesLoading: boolean;
  tripTypeOptions: ReadonlyArray<{ value: string; label: string }>;
}): React.JSX.Element => (
  <UzFormField name="tripTypeId">
    {({ field, error }) => (
      <UzSelect
        key={tripTypeOptions.length > 0 ? 'trip-types-ready' : 'trip-types-loading'}
        name={field.name}
        label="Trip category"
        hint="From the Trip Types master (FIT, Group, Cruise, etc.)."
        placeholder={tripTypesLoading ? 'Loading trip types…' : 'Select trip category'}
        value={(field.value as string | null) ?? ''}
        onValueChange={(v) => field.onChange(v === '' ? null : v)}
        options={tripTypeOptions}
        error={error}
        disabled={pending || tripTypesLoading}
      />
    )}
  </UzFormField>
);

/** Single destination picker (from the destinations master) bound to the form. */
const DestinationField = ({ pending }: CardProps): React.JSX.Element => {
  const destinationsQuery = useDestinationsList({
    page: 1,
    pageSize: 200,
    sortBy: 'name',
    sortDir: 'asc',
    isActive: true,
  });
  const options = useMemo(
    () => [
      { value: '', label: '- None -' },
      ...(destinationsQuery.data?.data ?? []).map((d) => ({ value: d.id, label: d.name })),
    ],
    [destinationsQuery.data],
  );
  return (
    <UzFormField name="destinationId">
      {({ field, error }) => (
        <UzCombobox
          name={field.name}
          label="Destination"
          placeholder="Select a destination…"
          searchPlaceholder="Search destinations…"
          value={(field.value as string | null) ?? ''}
          onValueChange={(v) => field.onChange(v === '' ? null : v)}
          options={options}
          loading={destinationsQuery.isLoading}
          hint="Each day’s city is picked from this destination’s country / state."
          error={error}
          disabled={pending}
        />
      )}
    </UzFormField>
  );
};

const BasicsCard = ({
  form,
  pending,
  tripTypesLoading,
  tripTypeOptions,
  numberOfNights,
  numberOfDays,
}: CardProps & {
  form: UseFormReturn<CreatePackageRequest>;
  tripTypesLoading: boolean;
  tripTypeOptions: ReadonlyArray<{ value: string; label: string }>;
  numberOfNights: number;
  numberOfDays: number;
}): React.JSX.Element => (
  <PackageCard title="Basics" description="Headline, slug, months and trip length.">
    <TripCategoryField
      pending={pending}
      tripTypesLoading={tripTypesLoading}
      tripTypeOptions={tripTypeOptions}
    />
    <UzFormField name="name">
      {({ field, error }) => (
        <UzInput
          {...field}
          value={(field.value as string | null | undefined) ?? ''}
          label="Name"
          placeholder="e.g. Magical Kashmir 6N/7D"
          error={error}
          required
          disabled={pending}
        />
      )}
    </UzFormField>
    <UzFormField name="slug">
      {({ field, error }) => (
        <UzInput
          {...field}
          value={(field.value as string | null | undefined) ?? ''}
          label="Slug"
          placeholder="lowercase-with-hyphens"
          hint="Editable. Use the wand to generate from the name."
          error={error}
          required
          disabled={pending}
          rightAdornment={
            <button
              type="button"
              title="Generate slug from name"
              aria-label="Generate slug from name"
              disabled={pending}
              onClick={() => field.onChange(slugify(form.getValues('name') ?? ''))}
              className="text-zinc-400 transition-colors hover:text-zinc-700 disabled:cursor-not-allowed disabled:opacity-50"
            >
              <Wand2 className="h-4 w-4" />
            </button>
          }
        />
      )}
    </UzFormField>
    <UzFormField name="shortDescription">
      {({ field, error }) => (
        <UzTextarea
          {...field}
          value={(field.value as string | null | undefined) ?? ''}
          label="Short description"
          rows={2}
          error={error}
          disabled={pending}
        />
      )}
    </UzFormField>
    <DestinationField pending={pending} />
    <UzFormField name="isMice">
      {({ field }) => (
        <UzSwitch
          label="MICE package"
          description="MICE packages decide which destinations appear in the MICE page's Destinations We Cover picker."
          checked={field.value === true}
          onCheckedChange={field.onChange}
          disabled={pending}
        />
      )}
    </UzFormField>
    <UzFormField name="preferredMonths">
      {({ field, error }) => {
        const selected = normalizePreferredMonths(field.value);
        const toggle = (m: (typeof MONTHS)[number]) => {
          const next = selected.includes(m) ? selected.filter((x) => x !== m) : [...selected, m];
          field.onChange(next);
        };
        return (
          <div>
            <span className="mb-2 block text-sm leading-none font-medium text-zinc-900">
              Preferred months
            </span>
            <div className="flex flex-wrap gap-1.5">
              {MONTHS.map((m) => {
                const on = selected.includes(m);
                return (
                  <button
                    key={m}
                    type="button"
                    disabled={pending}
                    onClick={() => toggle(m)}
                    className={
                      'h-8 rounded-md border px-3 text-xs font-medium transition-colors ' +
                      (on
                        ? 'border-zinc-900 bg-zinc-900 text-white'
                        : 'border-zinc-200 text-zinc-600 hover:bg-zinc-50')
                    }
                  >
                    {m}
                  </button>
                );
              })}
            </div>
            <p className="mt-1 text-xs text-zinc-500">Empty = available any month.</p>
            {error && <p className="mt-1 text-xs text-red-600">{error}</p>}
          </div>
        );
      }}
    </UzFormField>
    <div className="space-y-1.5">
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        <ReadOnlyField label="Nights" value={numberOfNights} />
        <ReadOnlyField label="Days" value={numberOfDays} />
      </div>
      <p className="text-xs text-zinc-500">Auto from the day-by-day itinerary.</p>
    </div>
  </PackageCard>
);

const PricingCard = ({
  form,
  pending,
  hidePackagePrice,
  priceRupees,
  discountRupees,
  onPriceChange,
  onDiscountChange,
}: CardProps & {
  form: UseFormReturn<CreatePackageRequest>;
  hidePackagePrice: boolean;
  priceRupees: string;
  discountRupees: string;
  onPriceChange: (v: string) => void;
  onDiscountChange: (v: string) => void;
}): React.JSX.Element => {
  // Effective price preview (after discount). Plain rupees - no paise conversion.
  const origRupees = priceRupees.trim() === '' ? null : Number(priceRupees);
  const discRupees = discountRupees.trim() === '' ? 0 : Number(discountRupees);
  const validOrig = origRupees !== null && Number.isFinite(origRupees);
  const showDiscount = validOrig && discRupees > 0;
  const effectiveRupees = showDiscount ? Math.max(0, (origRupees as number) - discRupees) : null;

  // Total price must always exceed the hotel + flight cost components.
  // useWatch (not form.watch) so this card re-renders live as the operator
  // edits the hotel / flight cost fields.
  const [hotelCost, flightCost] = useWatch({
    control: form.control,
    name: ['hotelCost', 'flightCost'],
  });
  const costFloor = Number(hotelCost ?? 0) + Number(flightCost ?? 0);
  const belowCostFloor = validOrig && costFloor > 0 && (origRupees as number) <= costFloor;

  return (
    <PackageCard
      title="Pricing"
      description={
        hidePackagePrice
          ? 'Hotel and flight cost components. Selling prices are set per variant on the Batches & variants tab.'
          : 'Amounts in ₹ (up to 2 decimals). Discount is subtracted from the price; the original price is shown crossed-out on the website.'
      }
    >
      {!hidePackagePrice && (
        <>
          <div className="grid gap-5 sm:grid-cols-2">
            <UzInput
              label="Total price (₹)"
              value={priceRupees}
              onChange={(e) => onPriceChange(e.target.value)}
              inputMode="decimal"
              placeholder="25000"
              hint="Must be greater than hotel + flight cost."
              error={
                (form.formState.errors.price?.message as string | undefined) ??
                (belowCostFloor
                  ? `Total price must exceed hotel + flight cost (${formatRupees(costFloor)})`
                  : undefined)
              }
              disabled={pending}
            />
            <UzInput
              label="Discount (₹, optional)"
              value={discountRupees}
              onChange={(e) => onDiscountChange(e.target.value)}
              inputMode="decimal"
              placeholder="1000"
              disabled={pending}
            />
          </div>
          {showDiscount && effectiveRupees !== null && (
            <p className="text-xs text-zinc-500">
              Website shows{' '}
              <span className="font-medium text-zinc-900">{formatRupees(effectiveRupees)}</span>{' '}
              with <span className="line-through">{formatRupees(origRupees as number)}</span>{' '}
              crossed out.
            </p>
          )}
        </>
      )}
      <div className="grid gap-5 sm:grid-cols-2">
        <RupeeField name="hotelCost" label="Hotel cost (₹, optional)" pending={pending} />
        <RupeeField name="flightCost" label="Flight cost (₹, optional)" pending={pending} />
      </div>
    </PackageCard>
  );
};

// ---------------------------------------------------------------------------
// Details - sidebar
// ---------------------------------------------------------------------------

const StatusCard = ({ pending }: CardProps): React.JSX.Element => (
  <PackageCard title="Status">
    <UzFormField name="status">
      {({ field }) => (
        <UzSelect
          label="Status"
          value={(field.value as PackageStatus | undefined) ?? 'DRAFT'}
          onValueChange={field.onChange}
          options={STATUS_OPTIONS}
          disabled={pending}
        />
      )}
    </UzFormField>
    <UzFormField name="rating">
      {({ field, error }) => (
        <UzNumberInput
          name={field.name}
          label="Rating (0-5)"
          precision={1}
          step={0.5}
          min={0}
          max={5}
          value={field.value === null || field.value === undefined ? '' : (field.value as number)}
          onValueChange={(v) => field.onChange(v ?? null)}
          hint="Decimals allowed, e.g. 4.5"
          error={error}
          disabled={pending}
        />
      )}
    </UzFormField>
  </PackageCard>
);

// ---------------------------------------------------------------------------
// Content - descriptions + SEO
// ---------------------------------------------------------------------------

const DescriptionsCard = ({ pending }: CardProps): React.JSX.Element => (
  <PackageCard title="Descriptions" description="Rich content shown on the package page.">
    <UzFormField name="overviewHtml">
      {({ field }) => (
        <RichTextEditor
          value={(field.value as string | null | undefined) ?? ''}
          onChange={field.onChange}
          label="Overview"
          folder="packages"
          disabled={pending}
        />
      )}
    </UzFormField>
    <UzFormField name="visaInsuranceHtml">
      {({ field }) => (
        <RichTextEditor
          value={(field.value as string | null | undefined) ?? ''}
          onChange={field.onChange}
          label="Visa & insurance"
          folder="packages"
          disabled={pending}
        />
      )}
    </UzFormField>
  </PackageCard>
);

const SeoCard = ({ pending }: CardProps): React.JSX.Element => (
  <PackageCard title="SEO" description="Meta tags for the public package page.">
    <UzFormField name="metaTitle">
      {({ field, error }) => (
        <UzInput
          {...field}
          value={(field.value as string | null | undefined) ?? ''}
          label="Meta title"
          error={error}
          disabled={pending}
        />
      )}
    </UzFormField>
    <UzFormField name="metaDescription">
      {({ field, error }) => (
        <UzTextarea
          {...field}
          value={(field.value as string | null | undefined) ?? ''}
          label="Meta description"
          rows={2}
          error={error}
          disabled={pending}
        />
      )}
    </UzFormField>
    <UzFormField name="metaKeywords">
      {({ field, error }) => (
        <UzInput
          {...field}
          value={(field.value as string | null | undefined) ?? ''}
          label="Meta keywords"
          error={error}
          disabled={pending}
        />
      )}
    </UzFormField>
    <UzFormField name="ogImageId">
      {({ field }) => (
        <div className="space-y-1.5">
          <label className="text-sm font-medium text-zinc-700">
            Social share image (Open Graph)
          </label>
          <MediaDropField
            value={(field.value as string | null) ?? null}
            kind="image"
            disabled={pending}
            onChange={(id) => field.onChange(id)}
          />
          <p className="text-xs text-zinc-500">
            Recommended 1200×630. Shown when the package link is shared on social media.
          </p>
        </div>
      )}
    </UzFormField>
  </PackageCard>
);

// ---------------------------------------------------------------------------
// Shared fields
// ---------------------------------------------------------------------------

/** Rupee text input bound to a money-string form field (up to 2 decimals). */
const RupeeField = ({
  name,
  label,
  pending,
}: {
  name: keyof CreatePackageRequest & string;
  label: string;
  pending: boolean;
}): React.JSX.Element => (
  <UzFormField name={name}>
    {({ field, error }) => (
      <UzInput
        name={field.name}
        value={(field.value as string | null | undefined) ?? ''}
        onChange={(e) => {
          const v = e.target.value;
          if (!MONEY_2DP_RE.test(v)) return; // accept up to 2 decimal places only
          field.onChange(v === '' ? null : v);
        }}
        onBlur={field.onBlur}
        label={label}
        inputMode="decimal"
        error={error}
        disabled={pending}
      />
    )}
  </UzFormField>
);

/** Read-only labelled value, styled to sit beside the form inputs. */
const ReadOnlyField = ({ label, value }: { label: string; value: number }): React.JSX.Element => (
  <div className="space-y-1.5">
    <span className="block text-sm font-medium text-zinc-700">{label}</span>
    <div className="flex h-9 items-center rounded-md border border-zinc-200 bg-zinc-50 px-3 text-sm text-zinc-900">
      {value}
    </div>
  </div>
);
